import { Typography } from '@/components/ui/typography'
import withSuspense from '@/hooks/withSuspense'
import { useIsReadOnly } from '@/lib/casl'
import { useFormContext } from '@/lib/form-contexts'
import { useProductSearchTags } from '@/lib/services/products/products.query'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/patterns/empty-state'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Package } from 'lucide-react'

const PREFIX = 'pages./products.blocks.main.0'
const products = (str) => `${PREFIX}.${str}`
const PRODUCT_TYPE_SCOPE_ALL = 'all'
const PRODUCT_TYPE_SCOPE_INCLUDE = 'include'

const productTypeScopeOptions = [
  { id: PRODUCT_TYPE_SCOPE_ALL, name: 'All product types' },
  { id: PRODUCT_TYPE_SCOPE_INCLUDE, name: 'Only selected Product Types' },
]

const normalizeProductTypeKeys = (value) => {
  if (!Array.isArray(value)) {
    return []
  }

  return [
    ...new Set(
      value.map((key) => (typeof key === 'string' ? key.trim() : '')).filter((key) => key !== '')
    ),
  ]
}

const productTypeScopeForProps = (props = {}) => {
  if (
    Object.prototype.hasOwnProperty.call(props, 'productTypeScope') &&
    props.productTypeScope != null
  ) {
    return props.productTypeScope === PRODUCT_TYPE_SCOPE_INCLUDE
      ? PRODUCT_TYPE_SCOPE_INCLUDE
      : PRODUCT_TYPE_SCOPE_ALL
  }

  if (normalizeProductTypeKeys(props.includedProductTypeKeys).length > 0) {
    return PRODUCT_TYPE_SCOPE_INCLUDE
  }

  return PRODUCT_TYPE_SCOPE_ALL
}

const sortProductTypes = (productTypes = []) =>
  [...productTypes].sort(
    (a, b) =>
      Number(a?.sort_order ?? 0) - Number(b?.sort_order ?? 0) ||
      String(a?.label ?? '').localeCompare(String(b?.label ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      }) ||
      String(a?.key ?? '').localeCompare(String(b?.key ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      })
  )

const productTypeSelectionCopy = {
  title: 'Product Types to Include',
  description: 'Show the disclaimer only for products with one of these Product Types.',
  emptyMessage:
    'No Product Types are available yet. With Only selected Product Types selected, the disclaimer will stay hidden until Product Types are available and selected.',
  countNoun: 'included',
  checkboxIdPrefix: 'product-disclaimer-included-product-type',
  scrollAreaAttribute: 'data-product-disclaimer-product-type-scroll-area',
}

function ProductTypeSelectionField({ productTypes, field, onChange }) {
  const { isReadOnly } = useIsReadOnly()
  const selectedProductTypeKeys = normalizeProductTypeKeys(field.state.value)
  const selectedProductTypeKeySet = new Set(selectedProductTypeKeys)
  const productTypeKeys = productTypes.map((productType) => productType.key).filter(Boolean)
  const selectedCount = selectedProductTypeKeys.length

  const handleChange = (nextValue) => {
    const normalizedValue = normalizeProductTypeKeys(nextValue)

    field.handleChange(normalizedValue)
    onChange?.(normalizedValue)
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <Typography variant="emphasis" className="flex min-w-0 items-center gap-2">
            <Package className="size-5 shrink-0" /> {productTypeSelectionCopy.title}
          </Typography>

          <div className="flex shrink-0 items-center gap-4 sm:pt-0.5">
            <Button
              type="button"
              variant="link"
              className="h-auto px-0"
              disabled={isReadOnly || productTypeKeys.length === 0}
              onClick={() => handleChange(productTypeKeys)}
            >
              Select all
            </Button>
            <Button
              type="button"
              variant="link"
              className="h-auto px-0"
              disabled={isReadOnly || selectedCount === 0}
              onClick={() => handleChange([])}
            >
              Clear all
            </Button>
          </div>
        </div>

        <Typography variant="supporting">{productTypeSelectionCopy.description}</Typography>
      </div>

      {productTypes.length > 0 ? (
        <ScrollArea
          {...{ [productTypeSelectionCopy.scrollAreaAttribute]: true }}
          className="h-fit max-h-[22rem] rounded-lg border border-border/70 bg-muted/10"
          viewportClassName="max-h-[22rem]"
        >
          <div className="grid grid-cols-1 divide-y divide-border/60">
            {productTypes.map((productType) => {
              const productTypeKey = productType.key
              const checkboxId = `${productTypeSelectionCopy.checkboxIdPrefix}-${productType.key}`

              return (
                <Label key={productType.key} htmlFor={checkboxId} appearance="choiceRow">
                  <Checkbox
                    id={checkboxId}
                    type="button"
                    checked={selectedProductTypeKeySet.has(productTypeKey)}
                    disabled={isReadOnly}
                    onCheckedChange={(isChecked) => {
                      if (isChecked) {
                        handleChange([...selectedProductTypeKeys, productTypeKey])
                        return
                      }

                      handleChange(selectedProductTypeKeys.filter((key) => key !== productTypeKey))
                    }}
                  />
                  <span>{productType.label}</span>
                </Label>
              )
            })}
          </div>
        </ScrollArea>
      ) : (
        <EmptyState
          size="compact"
          surface="transparent"
          title={productTypeSelectionCopy.emptyMessage}
        />
      )}

      <Typography variant="label" tone="muted">
        {selectedCount} Product Type{selectedCount === 1 ? '' : 's'}{' '}
        {productTypeSelectionCopy.countNoun}
      </Typography>
    </div>
  )
}

const ProductPagesForm = () => {
  const form = useFormContext()
  const { data: searchTagGroups = [] } = useProductSearchTags()

  const productTypeItems = sortProductTypes(
    searchTagGroups.find((group) => group.tag_type === 'product_type')?.tags ?? []
  )

  const setProductTypeScope = (nextScope) => {
    if (nextScope === PRODUCT_TYPE_SCOPE_ALL) {
      form.setFieldValue(products('props.includedProductTypeKeys'), [])
    }
  }

  const setProductTypeSelection = () => {
    form.setFieldValue(products('props.productTypeScope'), PRODUCT_TYPE_SCOPE_INCLUDE)
  }

  return (
    <div className="flex flex-col gap-y-6 mb-4">
      <form.AppField name={products('is_enabled')}>
        {(field) => (
          <field.SwitchInput
            label="Show product disclaimer"
            description="Shows this substitution notice on product pages before shoppers add an item to their cart."
          />
        )}
      </form.AppField>

      <form.AppField name={products('props.disclaimerText')}>
        {(field) => (
          <field.TextareaInput
            label="Disclaimer message"
            rows={5}
            placeholder="We strive to fulfill your order as pictured; however, substitutions may be necessary due to flower availability..."
          />
        )}
      </form.AppField>

      <div className="flex flex-col gap-y-2">
        <Typography variant="emphasis">Where to show it</Typography>
        <Typography variant="supporting">
          Keep the disclaimer on all Product Types, or choose the Product Types where the notice
          belongs.
        </Typography>
      </div>

      <form.Subscribe
        selector={(state) => {
          const props = state.values?.pages?.['/products']?.blocks?.main?.[0]?.props ?? {}

          return {
            productTypeScope: productTypeScopeForProps(props),
          }
        }}
      >
        {({ productTypeScope }) => {
          return (
            <>
              <form.AppField name={products('props.productTypeScope')}>
                {(field) => (
                  <field.RadioButtonGroup
                    id="product-disclaimer-product-type-scope"
                    options={productTypeScopeOptions}
                    displayValue={productTypeScope}
                    size="sm"
                    classNames={{
                      container: 'w-full sm:w-auto',
                      button: 'flex-1 sm:flex-none',
                    }}
                    onValueChange={setProductTypeScope}
                  />
                )}
              </form.AppField>

              {productTypeScope === PRODUCT_TYPE_SCOPE_INCLUDE ? (
                <form.AppField name={products('props.includedProductTypeKeys')}>
                  {(field) => (
                    <ProductTypeSelectionField
                      productTypes={productTypeItems}
                      field={field}
                      onChange={setProductTypeSelection}
                    />
                  )}
                </form.AppField>
              ) : null}
            </>
          )
        }}
      </form.Subscribe>
    </div>
  )
}

export default withSuspense(ProductPagesForm)
